import React, { useEffect, useRef } from 'react';
import {
  Modal,
  Card,
  CardContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import Image from 'next/image';
import { templates } from '@/utils';
import { currentStorySelector } from '@/store/selectors';
import { useSelector } from 'react-redux';

interface InspirationModalProps {
  showCard: boolean;
  setShowCard: (value: boolean) => void;
}

const InspirationModal: React.FC<InspirationModalProps> = ({ showCard, setShowCard }) => {
  const story = useSelector(currentStorySelector);
  const isMobile = useMediaQuery('(max-width:600px)');
  const cardRef = useRef<HTMLDivElement>(null);


  const typeOfStory = story?.story_details?.type_of_story;
  const storyTitle = story?.title || 'Story';

  const selectedTemplate = templates.find((template: any) => template.title === typeOfStory);
  const prompts = selectedTemplate ? selectedTemplate.prompts : [];
  const updatedPrompts = prompts.map((prompt: any) => prompt.replace('{{storyTitle}}', storyTitle));
  useEffect(() => {
  function handleClickOutside(event: MouseEvent) {
    if (
      cardRef.current &&
      !cardRef.current.contains(event.target as Node)
    ) {
      setShowCard(false);

    }
  }

  if (showCard) {
    document.addEventListener('mousedown', handleClickOutside);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [showCard]);

  return (
    <>
      {/* Show Modal on Mobile */}
      {isMobile && showCard && (
        <Modal  open={showCard} onClose={() => setShowCard(false)}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              width: '100vw',
            }}>
            <Card
              sx={{
                width: '100vw',
                height: '100vh',
                bgcolor: '#131544E5',
                opacity:0.9,
                color: 'white',
                borderRadius: 0,
                boxShadow: 3,
                display: 'flex',
                flexDirection: 'column',
              }}>
              {/* Header Section */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  height: 60,
                  mt: '50px',
                }}>
                {/* Close Button */}
                <IconButton onClick={() => setShowCard(false)} sx={{ position: 'absolute', top: 10, left: 10 }}>
                  <Image src='/icons/close.svg' alt='Close' width={20} height={20} />
                </IconButton>

                {/* Heading */}
                <Typography fontFamily={'DM Sans'} fontSize={'24px'} fontWeight={'400'}>
                  Need Inspiration?
                </Typography>
              </Box>

              {/* Card Content */}
              <CardContent sx={{ flexGrow: 1, overflowY: 'auto', padding: '0 20px 20px 20px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    textAlign: 'center',
                  }}>
                  <Typography
                    fontFamily={'DM Sans'}
                    fontSize={'16px'}
                    maxWidth={'200px'}
                    fontWeight={'600'}
                    lineHeight={1.4}
                    gutterBottom>
                    Here are some ideas to get you started:
                  </Typography>
                </Box>
                <List>
                  {updatedPrompts.map((text, index) => (
                    <ListItem key={index} sx={{ display: 'flex', alignItems: 'flex-start', pl: 1, gap: 0.5 }}>
                      <ListItemIcon
                        sx={{
                          minWidth: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mt: '2px',
                        }}>
                        <svg width='15' height='15' viewBox='0 0 24 24' fill='white' xmlns='http://www.w3.org/2000/svg'>
                          <circle cx='12' cy='12' r='4' />
                        </svg>
                      </ListItemIcon>
                      <ListItemText
                        primary={text}
                        sx={{
                          fontFamily: 'Inter',
                          fontSize: '16px',
                          fontWeight: 400,
                          color: '#F1F7FA',
                          m: 0,
                          wordBreak: 'break-word',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>
        </Modal>
      )}

      {/* Show Card on Desktop */}
      {!isMobile && showCard && (
        <Card
         ref={cardRef}
          sx={{
            width: 520,
            maxWidth: '100%',
            height: 239,
            bgcolor: '#0E1130',
            color: 'white',
            borderRadius: 3,
            boxShadow: 3,
            mx: 'auto',
          }}>
          <CardContent sx={{ marginBottom: '28px' }}>
            <Typography fontSize={'16px'} fontWeight={'600'} variant='h6' align='center' gutterBottom>
              Here are some ideas to get you started:
            </Typography>
            <List>
              {updatedPrompts.map((text, index) => (
                <ListItem key={index} sx={{ display: 'flex', alignItems: 'flex-start', pl: 1, gap: 1 }}>
                  <ListItemIcon
                    sx={{ minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
                    <svg width='15' height='15' viewBox='0 0 24 24' fill='white' xmlns='http://www.w3.org/2000/svg'>
                      <circle cx='12' cy='12' r='4' />
                    </svg>
                  </ListItemIcon>
                  <ListItemText
                    primary={text}
                    sx={{ fontSize: '16px', fontWeight: 400, m: 0, wordBreak: 'break-word' }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default InspirationModal;
